import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Divider,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import type { UserRead } from "store/api/generatedApi";

type AssignmentMode = "manual" | "auto";

interface AssignmentControlsProps {
  users: UserRead[];
  selectedPeriodIds: Set<number>;
  assignedPeriodsCount: number;
  isAssigning: boolean;
  isUnassigning: boolean;
  onManualAssign: (userId: string) => void;
  onUnassign: () => void;
  onUnassignAll: () => void;
  onAutoDistribute: (userIds: string[]) => void;
}

export const AssignmentControls = ({
  users,
  selectedPeriodIds,
  assignedPeriodsCount,
  isAssigning,
  isUnassigning,
  onManualAssign,
  onUnassign,
  onUnassignAll,
  onAutoDistribute,
}: AssignmentControlsProps) => {
  const { t } = useTenantTranslation();
  const [mode, setMode] = useState<AssignmentMode>("auto");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [autoAssignUserIds, setAutoAssignUserIds] = useState<string[]>([]);

  const handleManualAssignClick = () => {
    onManualAssign(selectedUserId);
    setSelectedUserId("");
  };

  const handleAutoDistributeClick = () => {
    onAutoDistribute(autoAssignUserIds);
    setAutoAssignUserIds([]);
  };

  return (
    <Paper sx={{ p: 2, width: 320, overflow: "auto" }}>
      <Stack spacing={2}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, newMode) => {
            if (newMode) setMode(newMode as AssignmentMode);
          }}
          size="small"
        >
          <ToggleButton value="auto" sx={{ width: "100%" }}>
            <AutoFixHighIcon sx={{ mr: 1 }} />
            {t("autoDistribute")}
          </ToggleButton>
          <ToggleButton value="manual" sx={{ width: "100%" }}>
            <PersonAddIcon sx={{ mr: 1 }} />
            {t("manualAssign")}
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === "manual" ? (
          <>
            <Typography variant="h6">{t("manualAssign")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("selectPeriodsAndAssign")}
            </Typography>
            {selectedPeriodIds.size > 0 && (
              <Alert severity="info">
                {t("selectedCount", {
                  count: selectedPeriodIds.size,
                })}
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>{t("selectUser")}</InputLabel>
              <Select
                value={selectedUserId}
                label={t("selectUser")}
                onChange={(e) => {
                  setSelectedUserId(e.target.value);
                }}
              >
                {users.map((user) => (
                  <MenuItem key={user.uid} value={user.uid}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleManualAssignClick}
                disabled={
                  !selectedUserId || selectedPeriodIds.size === 0 || isAssigning
                }
              >
                {t("assignSelected")}
              </Button>
              <Button
                variant="outlined"
                onClick={onUnassign}
                disabled={
                  selectedPeriodIds.size === 0 || isAssigning || isUnassigning
                }
              >
                {t("unassign")}
              </Button>
            </Stack>
            <Button
              variant="outlined"
              fullWidth
              onClick={onUnassignAll}
              disabled={
                assignedPeriodsCount === 0 || isAssigning || isUnassigning
              }
            >
              {t("unassignAll")}
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h6">{t("autoDistribute")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("autoDistributeDescription")}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>{t("selectReviewers")}</InputLabel>
              <Select
                multiple
                value={autoAssignUserIds}
                label={t("selectReviewers")}
                onChange={(e) => {
                  setAutoAssignUserIds(
                    typeof e.target.value === "string"
                      ? [e.target.value]
                      : e.target.value
                  );
                }}
                renderValue={(selected) =>
                  selected
                    .map((id) => users.find((u) => u.uid === id)?.name ?? id)
                    .join(", ")
                }
              >
                {users.map((user) => (
                  <MenuItem key={user.uid} value={user.uid}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              fullWidth
              onClick={handleAutoDistributeClick}
              disabled={autoAssignUserIds.length === 0 || isAssigning}
              startIcon={<AutoFixHighIcon />}
            >
              {t("distributeEvenly")}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={onUnassignAll}
              disabled={
                assignedPeriodsCount === 0 || isAssigning || isUnassigning
              }
            >
              {t("unassignAll")}
            </Button>
          </>
        )}

        <Divider />
      </Stack>
    </Paper>
  );
};
